import { useMemo } from 'react';
import { conflicts, peers } from '../sudoku';
import type { GameState } from '../game/types';
import type { SettingsPayload } from '../api/types';
import { Cell, type CellHighlight } from './Cell';

interface BoardProps {
  state: GameState;
  settings: SettingsPayload;
  onSelect: (index: number) => void;
  hintCells?: number[];
}

export function Board({ state, settings, onSelect, hintCells = [] }: BoardProps) {
  const { cells, givens, notes, selected } = state;
  const hintSet = useMemo(() => new Set(hintCells), [hintCells]);

  const conflictSet = useMemo(() => {
    const set = new Set<number>();
    for (let i = 0; i < 81; i++) {
      if ((cells[i] ?? 0) !== 0 && conflicts(cells, i).length > 0) set.add(i);
    }
    return set;
  }, [cells]);

  const peerSet = useMemo(
    () => (selected === null ? new Set<number>() : new Set(peers[selected] ?? [])),
    [selected],
  );

  const selectedValue = selected !== null ? (cells[selected] ?? 0) : 0;

  const highlightFor = (i: number): CellHighlight => {
    if (i === selected) return 'selected';
    if (hintSet.has(i)) return 'hint';
    if (settings.highlight_same_number && selectedValue !== 0 && cells[i] === selectedValue)
      return 'same';
    if (settings.highlight_peers && peerSet.has(i)) return 'peer';
    return 'normal';
  };

  return (
    <div className="board" role="grid" aria-label="Sudoku board">
      {cells.map((value, i) => (
        <Cell
          key={i}
          index={i}
          value={value ?? 0}
          given={(givens[i] ?? 0) !== 0}
          notes={notes[i] ?? []}
          highlight={highlightFor(i)}
          conflict={conflictSet.has(i)}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
}
